import React from "react";

/**
 * Single metric / KPI surface aligned with `cards.css` (`.card`, `.card-title`).
 * Use inside `.cards-wrapper` / `.cards-wrapper--sm` for dashboards and finance summaries.
 *
 * @param {object} props
 * @param {React.ReactNode} props.label
 * @param {React.ReactNode} props.value — main number or formatted string
 * @param {string} [props.valueClassName] — append to value element
 * @param {React.CSSProperties} [props.valueStyle] — e.g. semantic color
 * @param {React.ReactNode} [props.subtitle] — delta, period, or hint
 * @param {string} [props.wrapperClassName] — added to `.card`
 * @param {string} [props.regionAriaLabel] — `aria-label` for the region (defaults to plain-text label if string)
 * @param {() => void} [props.onClick] — when set, card is keyboard-activatable
 * @param {boolean} [props.active] — pressed / filter-active styling
 * @param {string} [props.title] — native tooltip (e.g. click hint on interactive cards)
 */
function MetricStatCard({
  label,
  value,
  valueClassName = "",
  valueStyle,
  subtitle = null,
  wrapperClassName = "",
  regionAriaLabel,
  role,
  onClick,
  active = false,
  title,
}) {
  const interactive = typeof onClick === "function";
  const resolvedRole = role ?? (interactive ? "button" : "group");

  const aria =
    regionAriaLabel != null
      ? regionAriaLabel
      : typeof label === "string" || typeof label === "number"
        ? String(label)
        : undefined;

  return (
    <div
      className={`card card--no-accent metric-stat-card${interactive ? " metric-stat-card--interactive" : ""}${
        active ? " metric-stat-card--active" : ""
      }${wrapperClassName ? ` ${wrapperClassName}` : ""}`}
      role={resolvedRole}
      aria-label={aria}
      aria-pressed={interactive ? active : undefined}
      title={title}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className="card-title metric-stat-card__label" style={{ color: "#334155" }}>
        {label}
      </div>
      <div
        className={`metric-stat-card__value${valueClassName ? ` ${valueClassName}` : ""}`.trim()}
        style={{ fontSize: 22, fontWeight: 700, ...valueStyle }}
      >
        {value}
      </div>
      {subtitle != null && subtitle !== false && (
        <div className="metric-stat-card__subtitle" style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

export default MetricStatCard;
